import { useEffect, useState } from "react";
import api from "../../services/api";
import { Event, addEvent } from "./Event";
import { Task, addTask } from "./Task";
import { ChallengeBlock, addChallenge } from "./ChallengeBlock";
import { Group, addGroup, addEventGroup, addTaskGroup } from "./Group";
import { useAuth } from "../context/auth/AuthState";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Plus, Edit } from "lucide-react";
import {
  validateChallenge,
  validateTask,
  validateEvent,
  validateGroup,
} from "../backendRequests/functionUtils";
import { useData } from "../context/data/DataState";

//stop map re-render after every change - rerender only on
// OR autofocus after every re-render

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

const convertDateKeysToFormattedStrings = (obj) => {
  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (Array.isArray(obj[key])) {
        // If the value is an array, process each element separately
        newObj[key] = obj[key].map((element) => {
          if (
            typeof element === "object" &&
            element !== null &&
            !(element instanceof Date)
          ) {
            // If the element is an object (excluding Date objects), recursively process it
            return convertDateKeysToFormattedStrings(element);
          } else if (element instanceof Date) {
            // If the element is a Date object, format it
            return formatDate(element); // Assuming formatDate function formats the date as desired
          } else {
            // Otherwise, keep the element as is
            return element;
          }
        });
      } else if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !(obj[key] instanceof Date)
      ) {
        // If the value is an object (excluding Date objects), recursively process it
        newObj[key] = convertDateKeysToFormattedStrings(obj[key]);
      } else {
        if (key.endsWith("date")) {
          // If the key ends with 'date', format the Date object
          const date = obj[key] instanceof Date ? obj[key] : new Date(obj[key]);
          newObj[key] = formatDate(date); // Assuming formatDate function formats the date as desired
        } else {
          // Otherwise, keep the value as is
          newObj[key] = obj[key];
        }
      }
    }
  }
  return newObj;
};

export const Challenge = ({
  formStates,
  setFormStates,
  setviewPrev,
  setviewNext,
  setalert,
}) => {
  const { updateData } = useData();
  const { currentUser } = useAuth();
  const [backDropInfo, setBackDropInfo] = useState({
    open: false,
    index: "00",
  });

  useEffect(() => {
    setviewPrev(true);
    setviewNext(false);

    return () => {
      setviewNext(false);
      setviewPrev(false);
    };
  }, []);

  const validateForm = () => {
    for (let i in formStates.challenge) {
      let challengeValid = validateChallenge(
        formStates.challenge[i],
        formStates.season
      );
      if (challengeValid.ok === false) {
        setalert({
          msg: (
            <>
              Error in Challenge {parseInt(i) + 1}: {challengeValid.message}
            </>
          ),
          type: "error",
        });
        return false;
      }
      for (let j in formStates.challenge[i].task) {
        let taskValid = validateTask(
          formStates.challenge[i].task[j],
          formStates.challenge[i]
        );
        if (taskValid.ok === false) {
          setalert({
            msg: (
              <>
                Error in Challenge {parseInt(i) + 1} Task {parseInt(j) + 1}:{taskValid.message}
              </>
            ),
            type: "error",
          });
          return false;
        }
      }
      for (let j in formStates.challenge[i].event) {
        let eventValid = validateEvent(
          formStates.challenge[i].event[j],
          formStates.challenge[i]
        );
        if (eventValid.ok === false) {
          setalert({
            msg: (
              <>
                Error in Challenge {parseInt(i) + 1} Event {parseInt(j) + 1}:{eventValid.message}
              </>
            ),
            type: "error",
          });
          return false;
        }
      }
      for (let j in formStates.challenge[i].group) {
        let groupValid = validateGroup(
          formStates.challenge[i].group[j],
          formStates.challenge[i]
        );
        if (groupValid.ok === false) {
          setalert({
            msg: (
              <>
                Error in Challenge {parseInt(i) + 1} Group {parseInt(j) + 1}:{groupValid.message}
              </>
            ),
            type: "error",
          });
          return false;
        }
      }
    }
    return true;
  };

  const mapChallenges = () =>
    formStates.challenge.map((ch, i) => {
      return (
        <div
          key={i}
          className="border border-gray-300 rounded-lg m-2 p-4 bg-white shadow-sm"
        >
          <ChallengeBlock
            key={i}
            i={i}
            ch={ch}
            formStates={formStates}
            setFormStates={setFormStates}
          />
          {/* --------------------Task------------------------------------ */}
          <h3 className="text-lg font-semibold mt-4 mb-2">Tasks</h3>
          {ch.task.map((taskItem, j) => {
            if (!taskItem.group_id) {
              return (
                <div key={j} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                  <Dialog
                    open={backDropInfo.open && backDropInfo.index === `t${i}${j}`}
                    onOpenChange={(open) => {
                      if (!open) {
                        setBackDropInfo({ open: false, index: "" });
                      }
                    }}
                  >
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogTitle>Edit Task</DialogTitle>
                      <Task
                        key={j}
                        i={i}
                        j={j}
                        formStates={formStates}
                        setFormStates={setFormStates}
                        taskItem={taskItem}
                      />
                    </DialogContent>
                  </Dialog>
                  <div className="flex-1">
                    {taskItem.task_name || `Task ${j + 1}`}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setBackDropInfo({
                        open: true,
                        index: `t${i}${j}`,
                      })
                    }
                  >
                    <Edit className="h-4 w-4 text-green-600" />
                  </Button>
                </div>
              );
            }
          })}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() =>
              addTask(i, setFormStates, "", {
                start_date: ch.start_date,
                end_date: ch.end_date,
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Task
          </Button>

          {/* --------------------Event------------------------------------ */}
          <h3 className="text-lg font-semibold mt-4 mb-2">Events</h3>
          {ch.event.map((eventItem, j) => {
            if (!eventItem.group_id) {
              return (
                <div key={j} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                  <Dialog
                    open={backDropInfo.open && backDropInfo.index === `e${i}${j}`}
                    onOpenChange={(open) => {
                      if (!open) {
                        setBackDropInfo({ open: false, index: "" });
                      }
                    }}
                  >
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogTitle>Edit Event</DialogTitle>
                      <Event
                        key={j}
                        i={i}
                        j={j}
                        formStates={formStates}
                        setFormStates={setFormStates}
                        eventItem={eventItem}
                      />
                    </DialogContent>
                  </Dialog>
                  <div className="flex-1">
                    {eventItem.event_name || `Event ${j + 1}`}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setBackDropInfo({
                        open: true,
                        index: `e${i}${j}`,
                      })
                    }
                  >
                    <Edit className="h-4 w-4 text-green-600" />
                  </Button>
                </div>
              );
            }
          })}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() =>
              addEvent(i, setFormStates, "", {
                start_date: ch.start_date,
                end_date: ch.end_date,
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Event
          </Button>

          {/* --------------------Groups------------------------------------ */}
          <h3 className="text-lg font-semibold mt-4 mb-2">Groups of Activities</h3>
          {ch.group.map((groupItem, j) => (
            <div
              key={j}
              className="border border-gray-200 rounded p-3 my-2 bg-gray-50"
            >
              <Group
                key={j}
                i={i}
                j={j}
                groupItem={groupItem}
                formStates={formStates}
                setFormStates={setFormStates}
              />
              {/* --------Group Tasks------------ */}
              <h6 className="text-sm font-semibold mt-3 mb-1">Group {j + 1} Tasks</h6>
              {ch.task.map((taskItem, k) => {
                if (groupItem.group_id === taskItem.group_id) {
                  return (
                    <div key={k} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                      <Dialog
                        open={backDropInfo.open && backDropInfo.index === `t${i}${k}`}
                        onOpenChange={(open) => {
                          if (!open) {
                            setBackDropInfo({ open: false, index: "" });
                          }
                        }}
                      >
                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                          <DialogTitle>Edit Group Task</DialogTitle>
                          <Task
                            key={k}
                            i={i}
                            j={k}
                            formStates={formStates}
                            setFormStates={setFormStates}
                            taskItem={taskItem}
                          />
                        </DialogContent>
                      </Dialog>
                      <div className="flex-1">
                        {taskItem.task_name || `Task ${k + 1}`}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setBackDropInfo({
                            open: true,
                            index: `t${i}${k}`,
                          })
                        }
                      >
                        <Edit className="h-4 w-4 text-green-600" />
                      </Button>
                    </div>
                  );
                }
              })}
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  let taskId = addTask(i, setFormStates, groupItem.group_id, {
                    start_date: ch.start_date,
                    end_date: ch.end_date,
                  });
                  addTaskGroup(i, j, setFormStates, taskId);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Task
              </Button>

              {/* ---------Group Events------------ */}
              <h6 className="text-sm font-semibold mt-3 mb-1">Group {j + 1} Events</h6>
              {ch.event.map((eventItem, k) => {
                if (groupItem.group_id === eventItem.group_id) {
                  return (
                    <div key={k} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                      <Dialog
                        open={backDropInfo.open && backDropInfo.index === `e${i}${k}`}
                        onOpenChange={(open) => {
                          if (!open) {
                            setBackDropInfo({ open: false, index: "" });
                          }
                        }}
                      >
                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                          <DialogTitle>Edit Group Event</DialogTitle>
                          <Event
                            key={k}
                            i={i}
                            j={k}
                            formStates={formStates}
                            setFormStates={setFormStates}
                            eventItem={eventItem}
                          />
                        </DialogContent>
                      </Dialog>
                      <div className="flex-1">
                        {eventItem.event_name || `Event ${k + 1}`}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setBackDropInfo({
                            open: true,
                            index: `e${i}${k}`,
                          })
                        }
                      >
                        <Edit className="h-4 w-4 text-green-600" />
                      </Button>
                    </div>
                  );
                }
              })}
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  let eventId = addEvent(i, setFormStates, groupItem.group_id, {
                    start_date: ch.start_date,
                    end_date: ch.end_date,
                  });
                  addEventGroup(i, j, setFormStates, eventId);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Event
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => addGroup(i, setFormStates)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Group
          </Button>
        </div>
      );
    });

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Challenges</h1>

      {mapChallenges()}

      <Button
        variant="default"
        className="mt-4"
        onClick={() => addChallenge(setFormStates, currentUser.user_id)}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add New Challenge
      </Button>

      <div className="mt-6">
        <Button
          variant="default"
          className="bg-green-600 hover:bg-green-700"
          onClick={() => {
            if (validateForm() === false) {
              return;
            }
            const formattedFormStates =
              convertDateKeysToFormattedStrings(formStates);
            formattedFormStates.season.community_id =
              formStates.community.community_id;
            
            api
              .post("/season/create", formattedFormStates)
              .then((response) => {
                setalert({
                  msg: "Form submitted successfully",
                  type: "success",
                });
                updateData();
              })
              .catch((err) => {
                setalert({
                  msg: err.response?.data || err.message,
                  type: "error",
                });
                console.error(err);
              });
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};
